import type { ConnectContact, ConnectConnection } from '@/types';
import { connectAdapter } from '@/connect/adapter/streamsConnectAdapter';

/** Lazily read CCP enums — avoids calling getCcpEnums() at module load (before CCP init). */
function ccp() {
  return connectAdapter.getCcpEnums();
}

/**
 * Returns true if the contact is in MISSED or ERROR state (missed call/chat or error before accept).
 * Connect may use ContactStateType.ERROR for missed calls; we treat both MISSED and ERROR as missed.
 */
export function isContactInMissedState(
  contact: ConnectContact | null
): boolean {
  if (!contact) return false;
  try {
    const enums = ccp();
    const state = contact.getState();
    const missedType = enums.ContactStateType.MISSED;
    const errorType = enums.ContactStateType.ERROR;
    const isMissed = missedType !== undefined && state?.type === missedType;
    const isError = errorType !== undefined && state?.type === errorType;
    return isMissed || isError;
  } catch {
    return false;
  }
}

/**
 * Returns true when contact status/state name indicates After Chat Work (ACW).
 * Some tenants expose this via getStatus().name (e.g. "After chat work"),
 * so we normalize casing/spaces/underscores.
 */
export function isContactInAfterChatWorkState(
  contact: ConnectContact | null
): boolean {
  if (!contact) return false;
  try {
    const normalize = (value?: string | null) =>
      String(value ?? '')
        .toLowerCase()
        .replace(/[\s_]+/g, '');
    const state = contact.getState?.();
    const status = contact.getStatus?.();
    const stateName = normalize(state?.name);
    const statusName = normalize(status?.name);
    if (stateName === 'afterchatwork' || statusName === 'afterchatwork') {
      return true;
    }
    // Streams often transitions chat contacts to ENDED during ACW; names may be empty.
    const endedType = ccp().ContactStateType.ENDED;
    const isEndedByType =
      endedType !== undefined &&
      (state?.type === endedType || status?.type === endedType);
    return isChatContact(contact) && isEndedByType && !contact.isConnected();
  } catch {
    return false;
  }
}

/**
 * Connection that is either connected or on hold (for duration, destroy, or fallback).
 * Returns undefined if the contact no longer exists (e.g. StateError after refresh/remote hang up).
 * Checks each connection safely so one bad connection doesn't make the whole contact look inactive.
 */
export function getActiveConnection(
  contact: ConnectContact | null
): ConnectConnection | undefined {
  if (!contact) return undefined;
  try {
    const connections = contact.getConnections();
    for (const conn of connections) {
      try {
        if (conn.isConnected() || conn.isOnHold?.()) return conn;
      } catch {
        // skip this connection, try next
      }
    }
    return undefined;
  } catch {
    return undefined;
  }
}

/** For two-party calls, hold/resume must be called on the initial (customer) connection so audio is correct. */
export function getHoldResumeConnection(
  contact: ConnectContact | null
): ConnectConnection | undefined {
  if (!contact) return undefined;
  try {
    const initial = contact.getInitialConnection?.();
    if (initial) return initial;
    return getActiveConnection(contact);
  } catch {
    return undefined;
  }
}

/** Safe check for active call leg; returns false if contact no longer exists (StateError). */
export function hasActiveCallLeg(contact: ConnectContact): boolean {
  if (getActiveConnection(contact)) return true;
  try {
    if (contact.isConnected?.()) return true;
    const stateType = String(contact.getState?.()?.type ?? '').toLowerCase();
    const statusType = String(contact.getStatus?.()?.type ?? '').toLowerCase();
    return stateType === 'connected' || statusType === 'connected';
  } catch {
    return false;
  }
}

/**
 * Root contact id for a transfer chain: getInitialContactId() first, then the
 * CaptureContactId attribute set by the contact flow (Connect sometimes omits
 * InitialContactId). Returns undefined when neither is present.
 */
export function getInitialOrCaptureContactId(
  contact: ConnectContact
): string | undefined {
  const initialContactId = contact.getInitialContactId?.();
  if (initialContactId) return initialContactId;
  const contactAttributes = contact.getAttributes?.();
  return contactAttributes?.['CaptureContactId']?.value;
}

/**
 * Returns true if the contact is a chat contact (Connect Streams ContactType.CHAT).
 * Chat contacts use the chat media controller for sendMessage/onMessage.
 */
export function isChatContact(contact: ConnectContact | null): boolean {
  if (!contact) return false;
  try {
    const chatType = ccp().ContactType.CHAT;
    const type = contact.getType?.();
    return chatType !== undefined && type === chatType;
  } catch {
    return false;
  }
}

/**
 * Returns the agent's chat connection for a CHAT contact, or undefined.
 * Use this connection with connect.core.mediaFactory.get(connection) to get the chat controller (sendMessage/onMessage).
 */
export function getChatConnection(
  contact: ConnectContact | null
): ConnectConnection | undefined {
  if (!contact || !isChatContact(contact)) return undefined;
  try {
    return contact.getAgentConnection?.() as ConnectConnection | undefined;
  } catch {
    return undefined;
  }
}
